import React from 'react';
import { AutocompleteElement, Controller } from 'react-hook-form-mui';

import { FormHelperText } from '@mui/material';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;

const TIMEZONES = [
  'UTC',
  'Etc/UTC',
  'Etc/GMT',
  'Etc/GMT+1',
  'Etc/GMT-1',
  'Etc/GMT+2',
  'Etc/GMT-2',
  'America/New_York',
  'America/Detroit',
  'America/Toronto',
  'America/Montreal',
  'America/Chicago',
  'America/Winnipeg',
  'America/Menominee',
  'America/Indiana/Knox',
  'America/Denver',
  'America/Phoenix',
  'America/Edmonton',
  'America/Los_Angeles',
  'America/Vancouver',
  'America/Anchorage',
  'America/Juneau',
  'America/Adak',
  'Pacific/Honolulu',
  'America/Mexico_City',
  'America/Monterrey',
  'America/Tijuana',
  'America/Cancun',
  'America/Chihuahua',
  'Europe/London',
  'Europe/Dublin',
  'Europe/Lisbon',
  'Atlantic/Canary',
  'Europe/Amsterdam',
  'Europe/Andorra',
  'Europe/Berlin',
  'Europe/Brussels',
  'Europe/Budapest',
  'Europe/Copenhagen',
  'Europe/Luxembourg',
  'Europe/Madrid',
  'Europe/Malta',
  'Europe/Oslo',
  'Europe/Paris',
  'Europe/Prague',
  'Europe/Rome',
  'Europe/Stockholm',
  'Europe/Vienna',
  'Europe/Warsaw',
  'Europe/Zagreb',
  'Europe/Zurich',
  'Europe/Athens',
  'Europe/Bucharest',
  'Europe/Helsinki',
  'Europe/Kyiv',
  'Europe/Riga',
  'Europe/Sofia',
  'Europe/Tallinn',
  'Europe/Vilnius',
  'Europe/Ljubljana',
  'Europe/Monaco',
  'Europe/San_Marino',
  'Europe/Vatican',
  'Europe/Istanbul',
  'Europe/Moscow',
];

export function FormSelect({ children }: { children: React.ReactNode }) {
  return <>{children}</>;
}
type TimezoneSelectProps = {
  onInputChange: (value: string) => void;
  name: string;
  label?: string;
  placeholder?: string;
  required?: boolean;
};

function TimezoneSelect({
  name,
  label,
  placeholder,
  required,
  onInputChange,
}: TimezoneSelectProps) {
  return (
    <AutocompleteElement
      name={name}
      options={TIMEZONES}
      label={label}
      required={required}
      textFieldProps={{
        placeholder: placeholder || 'Select timezone',
      }}
      autocompleteProps={{
        onChange: (_event, value) => {
          onInputChange(value || '');
        },
        filterOptions: (options, { inputValue }) =>
          options.filter((option) =>
            option.toLowerCase().includes(inputValue.toLowerCase())
          ),
        renderOption: (props, option) => (
          <MenuItem
            {...props}
            key={option}
            sx={{
              backgroundColor: 'white !important',
              '&.Mui-focused': { backgroundColor: 'rgba(69, 89, 167, 0.16) !important' },
              padding: '3px 16px 3px 16px',
            }}
          >
            <ListItemText primary={option} primaryTypographyProps={{ variant: 'body2' }} />
          </MenuItem>
        ),
      }}
    />
  );
}

interface MultiSelectProps {
  name: string;
  label?: string;
  placeholder?: string;
  options: { id: number | string; label: string }[];
  control?: any;
  formControlSx?: any;
  selectSx?: any;
  showCheckbox?: boolean;
  showAllItem?: boolean;
  required?: boolean;
  disabled?: boolean;
}

function MultiSelect({
  name,
  label,
  placeholder,
  options,
  control,
  formControlSx,
  selectSx,
  showAllItem,
  showCheckbox,
  required,
  disabled,
}: MultiSelectProps) {
  return (
    <FormControl sx={formControlSx}>
      <InputLabel>{label}</InputLabel>
      <Controller
        name={name}
        control={control}
        rules={{ required }}
        render={({ field: { name: fieldName, value = [], onChange: fieldChange }, fieldState }) => (
          <>
            <Select
              name={fieldName}
              value={value}
              onChange={(event) => {
                const values = event.target.value;

                if (values.includes('all')) {
                  if (value.length === options.length) fieldChange([]);
                  else fieldChange(options.map(({ id }) => id));
                } else {
                  fieldChange(event.target.value);
                }
              }}
              renderValue={() =>
                value.length > 0
                  ? options
                      .filter((item) => value.includes(item.id))
                      .map((item) => item.label)
                      .join(', ')
                  : placeholder
              }
              MenuProps={{
                PaperProps: {
                  sx: { maxHeight: ITEM_HEIGHT * 6 + ITEM_PADDING_TOP },
                },
                MenuListProps: { sx: { paddingY: 0 } },
              }}
              displayEmpty
              multiple
              sx={selectSx}
              disabled={disabled}
            >
              {showAllItem ? (
                <MenuItem key='all' value='all'>
                  {showCheckbox ? (
                    <Checkbox
                      checked={value.length === options.length}
                      sx={{ margin: '0 16px 0 0' }}
                    />
                  ) : null}
                  <ListItemText primary='All' primaryTypographyProps={{ variant: 'body2' }} />
                </MenuItem>
              ) : null}

              {options.map((item) => (
                <MenuItem key={item.id} value={item.id} sx={{ padding: '3px 16px 3px 16px' }}>
                  {showCheckbox ? (
                    <Checkbox checked={value.includes(item.id)} sx={{ margin: '0 16px 0 0' }} />
                  ) : null}
                  <ListItemText primary={item.label} primaryTypographyProps={{ variant: 'body2' }} />
                </MenuItem>
              ))}
            </Select>
            {fieldState.error && (
              <FormHelperText error>
                {fieldState.error.message}
              </FormHelperText>
            )}
          </>
        )}
      />
    </FormControl>
  );
}

FormSelect.MultiSelect = MultiSelect;
FormSelect.TimezoneSelect = TimezoneSelect;
